import React from 'react';
import { Video, CalendarCheck, BookOpen, Smartphone } from 'lucide-react';
import { Button } from './Button';
import { useNavigate } from 'react-router-dom';

export const Hero: React.FC = () => {
  const navigate = useNavigate();

  const handleGetApp = () => {
    navigate('/waiting-list');
  };

  return (
    <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 bg-gray-50/50 overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-100/30 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2 -z-10"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-purple-100/30 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2 -z-10"></div>

      <div className="container mx-auto px-6 text-center relative z-10">
        
        {/* Text Content */}
        <div className="max-w-4xl mx-auto mb-16 space-y-6 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-gray-200 shadow-sm text-primary text-xs font-bold uppercase tracking-wider mb-2">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
              1st Men’s Telehealth App for Men in Africa!
            </div>

            <h1 className="text-5xl md:text-7xl font-extrabold text-secondary leading-[1.1] tracking-tight">
              World-Class Healthcare, <br/>
              <span className="text-primary relative inline-block">
                Now in Your Pocket
              </span>
            </h1>
            
            <p className="text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed font-medium">
              Book appointments, get prescriptions, and track your health stats instantly. Join thousands of men taking control of their health today.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                 <Button 
                  onClick={handleGetApp}
                  className="px-8 py-4 text-xl gap-3 shadow-2xl shadow-primary/40 min-w-[240px]"
                 >
                    <Smartphone size={24} strokeWidth={2.5} />
                    <span className="font-bold">Get the App</span>
                 </Button>
            </div>
            
            <div className="pt-2">
               <button onClick={() => navigate('/contact')} className="text-sm font-semibold text-gray-400 hover:text-primary transition-colors">
                 Or continue to book on web &rarr;
               </button>
            </div>
        </div>

        {/* Visual Content - Hero Image */}
        <div className="relative max-w-5xl mx-auto mt-12 animate-fade-in-up-delay">
            {/* Main Image Container */}
            <div className="relative z-10 rounded-[2.5rem] overflow-hidden shadow-2xl shadow-blue-900/10 ring-1 ring-black/5 bg-white">
               <div className="aspect-[16/9] w-full relative">
                 <img 
                   src="https://09da45aa8861e9e09f066d7bf0bbd9f0.cdn.bubble.io/f1765302489443x476509434006264100/pexels-artempodrez-8989216.jpg" 
                   alt="Modern Medical Team" 
                   className="w-full h-full object-cover object-center transform hover:scale-105 transition-transform duration-[2s]"
                 />
                 <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
               </div>
            </div>

            {/* Floating Cards */}
            <div className="absolute -top-6 left-4 md:-left-6 bg-white/90 backdrop-blur-md p-3 pr-6 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] flex items-center gap-3 animate-float z-20 border border-white/50">
                <div className="bg-blue-50 p-2.5 rounded-xl text-primary">
                    <Video size={20} strokeWidth={2.5} />
                </div>
                <div className="text-left">
                    <p className="text-xs font-bold text-secondary">Video Consultations</p>
                    <p className="text-[10px] text-gray-500 font-medium">Connect instantly</p>
                </div>
            </div>

             <div className="absolute -top-4 right-4 md:-right-6 bg-white/90 backdrop-blur-md p-3 pr-6 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] flex items-center gap-3 animate-float-delayed z-20 border border-white/50">
                <div className="bg-green-50 p-2.5 rounded-xl text-green-600">
                    <CalendarCheck size={20} strokeWidth={2.5} />
                </div>
                <div className="text-left">
                    <p className="text-xs font-bold text-secondary">Appointment</p>
                    <p className="text-[10px] text-gray-500 font-medium">Confirmed today</p>
                </div>
            </div>

            <div className="absolute -bottom-4 right-8 md:-right-2 bg-white/90 backdrop-blur-md p-3 pr-6 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] flex items-center gap-3 animate-float z-20 border border-white/50">
                <div className="bg-purple-50 p-2.5 rounded-xl text-purple-600">
                    <BookOpen size={20} strokeWidth={2.5} />
                </div>
                <div className="text-left">
                    <p className="text-xs font-bold text-secondary">Health Insights</p>
                    <p className="text-[10px] text-gray-500 font-medium">Expert articles</p>
                </div>
            </div>

            <div className="absolute -bottom-6 left-8 md:-left-2 bg-white/90 backdrop-blur-md p-3 pr-6 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] flex items-center gap-3 animate-float-delayed z-20 border border-white/50">
                <div className="bg-orange-50 p-2.5 rounded-xl text-orange-500">
                    <Smartphone size={20} strokeWidth={2.5} />
                </div>
                <div className="text-left">
                    <p className="text-xs font-bold text-secondary">Convenience</p>
                    <p className="text-[10px] text-gray-500 font-medium">Care via app</p>
                </div>
            </div>
        </div>
      </div>
    </section>
  );
};